// buildStepDetails — pure builder of Detail_Pairs for a Trip_Step.
//
// Maps a step's `stepType` and its display-ready `source` record into the
// ordered array of `{ label, value }` pairs rendered in the third column of a
// Trip_Step (the KeyValuePairs block). The builder is a thin, total field →
// label mapper:
//
//   - It includes a pair for every required field of the step type that is
//     PRESENT in the source record.
//   - It OMITS any pair whose source value is absent (null/undefined/empty
//     string). It never fabricates a placeholder value.
//   - An unknown / unhandled step type returns an empty list.
//
// The derivation layer (deriveJourneySteps / eventToStep) is responsible for
// shaping `source` into display-ready values under the canonical field names
// documented below; this kernel performs no formatting of its own. Optional
// fields (hotel loyalty status, dining dress code) are mapped exactly like any
// other field — present ⇒ included, absent ⇒ omitted — which satisfies the
// "only when present" requirement automatically.
//
// Validates: Requirements 7.1, 7.2, 7.3, 7.4, 7.5, 7.6

/**
 * @typedef {Object} DetailPair
 * @property {string} label
 * @property {import('react').ReactNode} value
 */

/**
 * A single required field for a step-type family. `key` is the canonical
 * source field name; `aliases` are alternate field names checked only when the
 * canonical key is absent (kept minimal so they never collide with generated
 * test records that use the canonical names).
 *
 * @typedef {Object} FieldSpec
 * @property {string} label
 * @property {string} key
 * @property {string[]} [aliases]
 */

// ── Per-family field specifications ─────────────────────────
// Order here defines the order of the emitted Detail_Pairs.

/** @type {FieldSpec[]} */
const FLIGHT_FIELDS = [
  { label: 'Gate', key: 'gate', aliases: ['departureGate'] },
  { label: 'Flight', key: 'flight', aliases: ['flightNumber'] },
  { label: 'Seat', key: 'seat', aliases: ['seatAssignment'] },
  { label: 'Boarding group', key: 'boardingGroup' },
  { label: 'Departure time', key: 'departureTime' },
];

/** @type {FieldSpec[]} */
const GROUND_TRANSPORT_FIELDS = [
  { label: 'Pickup', key: 'pickupLocation', aliases: ['pickup'] },
  { label: 'Service', key: 'service', aliases: ['company'] },
  { label: 'Estimated travel time', key: 'travelTime' },
  { label: 'Confirmation', key: 'confirmationNumber', aliases: ['confirmation'] },
];

/** @type {FieldSpec[]} */
const HOTEL_FIELDS = [
  { label: 'Hotel', key: 'name' },
  { label: 'Address', key: 'address' },
  { label: 'Confirmation', key: 'confirmationNumber', aliases: ['confirmation'] },
  { label: 'Room type', key: 'roomType' },
  // Optional: emitted only when present in the source.
  { label: 'Loyalty status', key: 'loyaltyStatus' },
];

/** @type {FieldSpec[]} */
const DINING_FIELDS = [
  { label: 'Restaurant', key: 'name', aliases: ['restaurant'] },
  { label: 'Address', key: 'address' },
  { label: 'Confirmation', key: 'confirmationNumber', aliases: ['confirmation'] },
  { label: 'Party size', key: 'partySize' },
  // Optional: emitted only when present in the source.
  { label: 'Dress code', key: 'dressCode' },
];

/** @type {FieldSpec[]} */
const PARKING_FIELDS = [
  { label: 'Parking lot', key: 'lot', aliases: ['parkingLot'] },
  { label: 'Level', key: 'level' },
  { label: 'Confirmation', key: 'confirmationNumber', aliases: ['confirmation'] },
  { label: 'Shuttle wait', key: 'shuttleWait' },
];

/** @type {FieldSpec[]} */
const LEAVE_HOME_FIELDS = [
  { label: 'Estimated drive', key: 'driveTime', aliases: ['travelTime'] },
  { label: 'Distance', key: 'distance' },
];

// ── stepType → field-spec family ────────────────────────────
// Step types not present here (e.g. 'security', 'meeting', 'arrive_home') and
// any unknown value resolve to an empty detail list.

const STEP_TYPE_FIELDS = {
  // Flight family — departure gate, arrival, and landing all surface the same
  // flight detail keys.
  gate: FLIGHT_FIELDS,
  flight_arrival: FLIGHT_FIELDS,
  landing: FLIGHT_FIELDS,

  ground_transport: GROUND_TRANSPORT_FIELDS,

  hotel_checkin: HOTEL_FIELDS,
  hotel_checkout: HOTEL_FIELDS,

  dining: DINING_FIELDS,

  parking: PARKING_FIELDS,

  leave_home: LEAVE_HOME_FIELDS,
};

// ── Helpers ─────────────────────────────────────────────────

/**
 * A source value is "present" when it is neither null/undefined nor an
 * empty/whitespace-only string. Numbers (including 0) and React nodes count as
 * present. This guards against fabricating pairs for absent data.
 *
 * @param {*} value
 * @returns {boolean}
 */
function isPresent(value) {
  if (value == null) return false;
  if (typeof value === 'string') return value.trim() !== '';
  return true;
}

/**
 * Resolve the value for a field spec from the source record, preferring the
 * canonical key and falling back to declared aliases. Returns `undefined` when
 * no present value exists.
 *
 * @param {Object} source
 * @param {FieldSpec} spec
 * @returns {*}
 */
function resolveValue(source, spec) {
  if (isPresent(source[spec.key])) return source[spec.key];
  if (Array.isArray(spec.aliases)) {
    for (const alias of spec.aliases) {
      if (isPresent(source[alias])) return source[alias];
    }
  }
  return undefined;
}

// ── Main ────────────────────────────────────────────────────

/**
 * Build the ordered Detail_Pairs for a Trip_Step.
 *
 * @param {string} stepType  One of the recognized step types (e.g. 'gate',
 *                           'ground_transport', 'hotel_checkin', 'dining',
 *                           'parking', 'leave_home'). Unknown types yield [].
 * @param {Object} [source]  Display-ready source record keyed by the canonical
 *                           field names documented above. Absent fields are
 *                           omitted from the result.
 * @param {Object} [ctx]     Optional context (unused by the base mapper today;
 *                           reserved for future contextual detail enrichment so
 *                           the signature stays stable for callers).
 * @returns {DetailPair[]}   Ordered key-value pairs; empty for unknown types.
 */
export function buildStepDetails(stepType, source = {}, ctx = {}) {
  void ctx; // reserved; present for a stable calling contract
  // Use an own-property lookup so inherited Object.prototype names passed as a
  // stepType (e.g. 'valueOf', 'toString', 'constructor') resolve to "unknown"
  // rather than a prototype method. Guard on Array shape as a final safety net.
  const specs = Object.prototype.hasOwnProperty.call(STEP_TYPE_FIELDS, stepType)
    ? STEP_TYPE_FIELDS[stepType]
    : undefined;
  if (!Array.isArray(specs)) return [];

  const record = source && typeof source === 'object' ? source : {};

  const pairs = [];
  for (const spec of specs) {
    const value = resolveValue(record, spec);
    if (value === undefined) continue; // absent ⇒ omit, never fabricate
    pairs.push({ label: spec.label, value });
  }
  return pairs;
}

export default buildStepDetails;
